import { Injectable } from '@angular/core';
import { DoctorStore } from './state/doctor-store';
import { DoctorQuery } from './state/doctor-query';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { LoggerService } from '@core/services/logger/logger.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ShellState, ShellStore } from '@shared/store/shell/shell-store';
import { Doctor } from '@shared/models/doctor';
import type { Maybe } from '@/types/globals';

@Injectable()
export class DoctorFacade {
	doctor$: Observable<Doctor> = this.query.doctor$;
	reassignedDoctor$: Observable<Doctor | null> = this.query.reassignedDoctor$;
	isLicenseEditable$: Observable<boolean> = this.query.isLicenseEditable$;
	isReadOnly$: Observable<boolean> = this.shellQuery.getReadOnlyState('doctor');
	isRxValidForSave$: Observable<Maybe<boolean>> = this.shellQuery.isRxValidForSave$;
	doctorInfo$: Observable<string | null | undefined> = this.query.doctor$.pipe(
		map(doctor => {
			const doctorName = doctor?.Name?.trim();
			const doctorLicense = doctor?.Licence?.trim();

			return [doctorName, doctorLicense].filter(Boolean).join(' | ');
		}),
		distinctUntilChanged()
	);

	license$: Observable<string | undefined> = this.query.doctor$.pipe(
		map(doctor => doctor?.Licence),
		distinctUntilChanged()
	);

	readonly facadeName = 'DoctorFacade';

	constructor(
		readonly store: DoctorStore,
		readonly query: DoctorQuery,
		readonly shellStore: ShellStore,
		readonly shellQuery: ShellQuery,
		readonly logger: LoggerService
	) {}

	getDoctorInformation(): Observable<Doctor> {
		return this.shellQuery.doctor$.pipe(
			tap((doctor: Doctor) => {
				if (doctor) {
					this.store.update({ doctor });
				} else {
					this.logger.warn(`${this.facadeName}: doctor failed to load`, { module: this.facadeName });
				}
			})
		);
	}

	updateLicense({ license }: { license: string }): void {
		this.shellStore.update(state => {
			return { ...state, userSettings: { ...state.userSettings, LicenseNumber: license } } as ShellState;
		});
	}

	updateReassignedDoctor(doctor: Doctor | null): void {
		this.store.update({ reassignedDoctor: doctor });
	}
}
